import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Card, Modal, Form, Input, Select, 
  message, Typography, Space, Popconfirm, Tag 
} from 'antd';
import { 
  UserOutlined, LockOutlined, PlusOutlined, 
  EditOutlined, DeleteOutlined, KeyOutlined 
} from '@ant-design/icons';
import { userApi } from '../services/userService';
import { formatDuration } from '../utils/formatUtils';

const { Title } = Typography;
const { Option } = Select;



const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [form] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();
  const [modalTitle, setModalTitle] = useState('');
  const [modalType, setModalType] = useState('create'); // 'create' or 'edit'

  // 获取所有用户
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await userApi.getAllUsers();
      console.log('获取用户列表响应:', response);
      
      // 处理后端返回的统一格式数据
      if (response && response.data && response.data.items) {
        setUsers(response.data.items);
      } else {
        setUsers([]);
        console.error('响应数据格式不正确:', response);
      }
    } catch (err) {
      console.error('获取用户列表失败:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        message.error('没有访问权限');
        // 不要在这里直接跳转，让 ProtectedRoute 组件处理
      } else {
        message.error('获取用户列表失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 初始化时获取用户列表
  useEffect(() => {
    fetchUsers();
  }, []);

  // 打开创建用户模态框
  const showCreateModal = () => {
    setModalType('create');
    setModalTitle('创建新用户');
    form.resetFields();
    setModalVisible(true);
  };

  // 打开编辑用户模态框
  const showEditModal = (user) => {
    setModalType('edit');
    setModalTitle('编辑用户');
    setCurrentUser(user);
    form.setFieldsValue({
      username: user.username,
      fullname: user.fullname,
      user_group: user.user_group
    });
    setModalVisible(true);
  };

  // 打开重置密码模态框
  const showResetPasswordModal = (user) => {
    setCurrentUser(user);
    resetPasswordForm.resetFields();
    setResetPasswordModalVisible(true);
  };

  // 处理模态框确认
  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (modalType === 'create') {
        await userApi.createUser(values);
        message.success('用户创建成功');
      } else {
        await userApi.updateUser(currentUser.id, values);
        message.success('用户更新成功');
      }
      
      setModalVisible(false);
      fetchUsers();
    } catch (err) {
      message.error('操作失败: ' + (err.response?.data?.detail || err.message));
    }
  };

  // 处理重置密码
  const handleResetPassword = async () => {
    try {
      const values = await resetPasswordForm.validateFields();
      console.log('重置密码请求数据:', { new_password: values.password });
      const response = await userApi.resetPassword(currentUser.id, { new_password: values.password });
      console.log('重置密码响应:', response);
      message.success('密码重置成功');
      setResetPasswordModalVisible(false);
    } catch (err) {
      console.error('重置密码失败:', err);
      const errorMsg = err.response?.data?.message || err.message || '密码重置失败';
      message.error(`密码重置失败: ${errorMsg}`);
    }
  };

  // 处理删除用户
  const handleDeleteUser = async (userId) => {
    try {
      await userApi.deleteUser(userId);
      message.success('用户删除成功');
      fetchUsers();
    } catch (err) {
      message.error('删除失败: ' + (err.response?.data?.detail || err.message));
    }
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '姓名',
      dataIndex: 'fullname',
      key: 'fullname',
    },
    {
      title: '用户组',
      dataIndex: 'user_group',
      key: 'user_group',
      render: (text) => (
        <Tag color={text === 'admin' ? 'red' : 'blue'}>
          {text === 'admin' ? '管理员' : '普通用户'}
        </Tag>
      ),
    },
    {
      title: '上次登录时间',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (text) => text ? new Date(text).toLocaleString() : '从未登录',
    },
    {
      title: '大模型使用量',
      dataIndex: 'token_usage',
      key: 'token_usage',
      render: (text) => `${text.toLocaleString()} token`,
    },
    {
      title: '文本解析量',
      dataIndex: 'page_usage',
      key: 'page_usage',
      render: (text) => `${text.toLocaleString()} 页`,
    },
    {
      title: '网课审核时长',
      dataIndex: 'video_audit_duration',
      key: 'video_audit_duration',
      render: (text) => formatDuration(text),
    },
    {
      title: '下载接口使用时长',
      dataIndex: 'plaso_api_duration',
      key: 'plaso_api_duration',
      render: (text) => formatDuration(text || 0),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => showEditModal(record)}
          >
            编辑
          </Button>
          <Button 
            type="link" 
            icon={<KeyOutlined />} 
            onClick={() => showResetPasswordModal(record)}
          >
            重置密码
          </Button>
          <Popconfirm
            title="确定要删除此用户吗？"
            onConfirm={() => handleDeleteUser(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              danger 
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    }
  ];

  return (
    <div>
      <Title level={2}>用户管理</Title>
      
      <Card style={{ marginBottom: 16 }}>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={showCreateModal}
        >
          创建新用户
        </Button>
      </Card>
      
      <Card>
        <Table 
          columns={columns} 
          dataSource={users} 
          rowKey="id" 
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* 创建/编辑用户模态框 */}
      <Modal
        title={modalTitle}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
            disabled={modalType === 'edit'}
          >
            <Input prefix={<UserOutlined />} placeholder="用户名" disabled={modalType === 'edit'} />
          </Form.Item>

          <Form.Item
            name="fullname"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="姓名" />
          </Form.Item>

          {modalType === 'create' && (
            <Form.Item
              name="password"
              label="密码"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码长度不能少于6个字符' }
              ]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="密码" />
            </Form.Item>
          )}

          <Form.Item
            name="user_group"
            label="用户组"
            rules={[{ required: true, message: '请选择用户组' }]}
          >
            <Select placeholder="请选择用户组">
              <Option value="user">普通用户</Option>
              <Option value="admin">管理员</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 重置密码模态框 */}
      <Modal
        title="重置密码"
        open={resetPasswordModalVisible}
        onOk={handleResetPassword}
        onCancel={() => setResetPasswordModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <p>您正在为用户 <strong>{currentUser?.username}</strong> 重置密码</p>
        <Form
          form={resetPasswordForm}
          layout="vertical"
        >
          <Form.Item
            name="password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码长度不能少于6个字符' }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="新密码" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="确认新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement; 